import * as ToggleGroup from "@radix-ui/react-toggle-group";
import type { FC } from "react";
import React from "react";
import {
  ComputerIcon,
  MoonIcon,
  SunIcon,
} from "@/components/ui/icons/ThemeIcons";
import { ColorScheme, useTheme } from "@/context/ThemeContext";
import { useThemePreview } from "@/hooks/useThemePreview";
import type { BaseComponentProps } from "@/types/component-props";
import { cn } from "@/utils/cn";
import { themeComponentStyles } from "@/utils/themeUtils";

export interface SchemeToggleGroupProps extends BaseComponentProps {
  /** Whether to show labels next to icons (default: false) */
  showLabels?: boolean;
  /** Delay in ms before showing color scheme preview when hovering (default: 450ms) */
  previewDelay?: number;
  /** Whether to disable the preview functionality (default: false) */
  disablePreview?: boolean;
}

/**
 * ColorSchemeSelector
 *
 * A component for switching between light, dark, and system color schemes (light/dark mode).
 * This component controls the color scheme (light/dark), NOT the theme colors/palette.
 * Uses Radix UI's ToggleGroup component for accessibility and state management.
 */
export const SchemeToggleGroup: FC<SchemeToggleGroupProps> = ({
  className,
  showLabels = false,
  previewDelay = 450,
  disablePreview = false,
}) => {
  const { colorScheme, setColorScheme, previewColorScheme } = useTheme();

  // Use theme preview hook to manage preview behavior
  const { handleMouseEnter: baseHandleMouseEnter, handleMouseLeave } =
    useThemePreview(previewColorScheme, {
      delay: previewDelay,
      disabled: disablePreview,
    });

  // Handle value change
  const handleValueChange = (value: string) => {
    if (value) {
      setColorScheme(value as ColorScheme);
    }
  };

  // Extended version of handleMouseEnter that skips if it's the active scheme
  const handleMouseEnter = React.useCallback(
    (scheme: ColorScheme) => {
      // Skip if it's already the active scheme
      if (scheme === colorScheme) return;

      // Otherwise use the generic preview handler
      baseHandleMouseEnter(scheme);
    },
    [colorScheme, baseHandleMouseEnter],
  );

  return (
    <ToggleGroup.Root
      aria-label="Select color scheme"
      className={cn(
        "inline-flex items-center justify-center p-0.5",
        themeComponentStyles.getContainerClasses({
          isSchemeToggleGroup: true,
        }),
        className,
      )}
      onValueChange={handleValueChange}
      type="single"
      value={colorScheme}
    >
      <ToggleGroup.Item
        aria-label="Light mode"
        className={cn(
          "inline-flex items-center justify-center rounded-sm px-3 py-1.5 text-sm font-medium transition-standard cursor-pointer mx-0.5",
          themeComponentStyles.getControlClasses(),
          "radix-state-on:bg-white dark:radix-state-on:bg-gray-900 radix-state-on:text-theme-primary dark:radix-state-on:text-theme-primary radix-state-on:shadow-[0_1px_2px_0_rgba(0,0,0,0.05)] dark:radix-state-on:shadow-[0_1px_3px_0_rgba(0,0,0,0.4)] font-medium",
          "radix-state-off:text-theme-muted-foreground",
          "hover:bg-theme-muted/70",
        )}
        onMouseEnter={() => handleMouseEnter(ColorScheme.LIGHT)}
        onMouseLeave={handleMouseLeave}
        value={ColorScheme.LIGHT}
      >
        <SunIcon className={cn("h-4 w-4", showLabels && "mr-1")} />
        {showLabels && <span>Light</span>}
      </ToggleGroup.Item>
      <ToggleGroup.Item
        aria-label="System preference"
        className={cn(
          "inline-flex items-center justify-center rounded-sm px-3 py-1.5 text-sm font-medium transition-standard cursor-pointer mx-0.5",
          themeComponentStyles.getControlClasses(),
          "radix-state-on:bg-white dark:radix-state-on:bg-gray-900 radix-state-on:text-theme-primary dark:radix-state-on:text-theme-primary radix-state-on:shadow-[0_1px_2px_0_rgba(0,0,0,0.05)] dark:radix-state-on:shadow-[0_1px_3px_0_rgba(0,0,0,0.4)] font-medium",
          "radix-state-off:text-theme-muted-foreground",
          "hover:bg-theme-muted/70",
        )}
        onMouseEnter={() => handleMouseEnter(ColorScheme.SYSTEM)}
        onMouseLeave={handleMouseLeave}
        value={ColorScheme.SYSTEM}
      >
        <ComputerIcon className={cn("h-4 w-4", showLabels && "mr-1")} />
        {showLabels && <span>System</span>}
      </ToggleGroup.Item>
      <ToggleGroup.Item
        aria-label="Dark mode"
        className={cn(
          "inline-flex items-center justify-center rounded-sm px-3 py-1.5 text-sm font-medium transition-standard cursor-pointer mx-0.5",
          themeComponentStyles.getControlClasses(),
          "radix-state-on:bg-white dark:radix-state-on:bg-gray-900 radix-state-on:text-theme-primary dark:radix-state-on:text-theme-primary radix-state-on:shadow-[0_1px_2px_0_rgba(0,0,0,0.05)] dark:radix-state-on:shadow-[0_1px_3px_0_rgba(0,0,0,0.4)] font-medium",
          "radix-state-off:text-theme-muted-foreground",
          "hover:bg-theme-muted/70",
        )}
        onMouseEnter={() => handleMouseEnter(ColorScheme.DARK)}
        onMouseLeave={handleMouseLeave}
        value={ColorScheme.DARK}
      >
        <MoonIcon className={cn("h-4 w-4", showLabels && "mr-1")} />
        {showLabels && <span>Dark</span>}
      </ToggleGroup.Item>
    </ToggleGroup.Root>
  );
};
